import i18next from 'i18next';
import { Fragment } from 'react';
import { Badge, Button } from 'react-bootstrap';
import ToolTip from 'src/components/Tooltip/Tooltip';
import type { CatalogRow } from 'src/motornet/types';
import { formatCurrency, formatDate } from 'src/utils';
import {
	Chip,
	CHIP_LABELS,
	CHIPS,
	ChipState,
	chipState,
	inactiveLinkReason,
	manufacturerCodes,
	rowYears,
	unavailableProvider,
} from './helpers';
import MatrixAutoRenewSwitch from './MatrixAutoRenewSwitch';

export type ExpandedChip = { versionKey: string; chip: Chip } | null;

type CellProps<V = unknown> = {
	value: V;
	row: { original: CatalogRow; index: number };
};

const textCell = ({ value }: CellProps<string | number | null>) => value ?? '-';

const CHIP_VARIANT: Record<ChipState, string> = {
	automatic: 'outline-success',
	manual: 'outline-success',
	missing: 'outline-secondary',
	unavailable: 'outline-warning',
};

const CHIP_ICON: Record<ChipState, string> = { automatic: '✓', manual: '✓', missing: '+', unavailable: '!' };

const unavailableTitle = (row: CatalogRow, chip: Chip): string => {
	const marked = chip === 'motornet' ? null : unavailableProvider(row, chip);
	if (!marked) return i18next.t('vehicle_association_chip_unavailable', { defaultValue: 'Segnalato come non disponibile' });
	const by = i18next.t('vehicle_association_unavailable_by', {
		user: marked.markedBy,
		date: formatDate(marked.markedAt, i18next.language, true),
		defaultValue: 'Segnato come non disponibile da {{user}} il {{date}}',
	});
	return marked.reason ? `${by}: ${marked.reason}` : by;
};

const chipTitle = (row: CatalogRow, chip: Chip, state: Exclude<ChipState, 'unavailable'>): string =>
	state === 'automatic'
		? i18next.t('vehicle_association_chip_automatic', { defaultValue: 'Trovato dal match automatico' })
		: state === 'manual'
		? i18next.t('vehicle_association_chip_manual', { defaultValue: 'Aggiunto a mano' })
		: (chip !== 'motornet' && inactiveLinkReason(row, chip)) ||
		  i18next.t('vehicle_association_chip_missing', { defaultValue: 'Da associare' });

export const createColumnsData = (
	expanded: ExpandedChip,
	onToggle: (versionKey: string, chip: Chip) => void
) => [
	{
		width: 100,
		accessor: 'make',
		id: 'make',
		Header: i18next.t('brand', { defaultValue: 'Marca' }),
		disableSortBy: true,
		Cell: textCell,
	},
	{
		width: 120,
		accessor: 'model',
		id: 'model',
		Header: i18next.t('model', { defaultValue: 'Modello' }),
		disableSortBy: true,
		Cell: textCell,
	},
	{
		width: 220,
		accessor: 'version',
		id: 'version',
		Header: i18next.t('version', { defaultValue: 'Versione' }),
		disableSortBy: true,
		Cell: ({ value }: CellProps<string | null>) => (
			<div className="text-wrap" style={{ minWidth: 180, maxWidth: 240 }}>
				{value ?? '-'}
			</div>
		),
	},
	{
		width: 70,
		id: 'modelYear',
		Header: i18next.t('year', { defaultValue: 'Anno' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => rowYears(row.original).join(' / ') || '-',
	},
	{
		width: 110,
		accessor: 'listPrice',
		id: 'listPrice',
		Header: i18next.t('vehicle_association_list_price', { defaultValue: 'Listino netto' }),
		disableSortBy: true,
		Cell: ({ value }: CellProps<number | null>) => (value == null ? '-' : formatCurrency(value)),
	},
	{
		width: 90,
		id: 'vehicleCode',
		Header: i18next.t('vehicle_association_vehicle_code', { defaultValue: 'Cod. veicolo' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => row.original.motornet?.vehicleCode ?? '-',
	},
	{
		width: 100,
		id: 'manufacturerCode',
		Header: i18next.t('vehicle_association_manufacturer_code', { defaultValue: 'Cod. costr.' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => {
			const codes = manufacturerCodes(row.original);
			return codes.length ? codes.map((code) => <div key={code}>{code}</div>) : '-';
		},
	},
	{
		width: 90,
		id: 'units',
		Header: i18next.t('vehicle_association_units', { defaultValue: 'Quot. / Disp.' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => {
			const quotable = row.original.motornet?.quotableUnits ?? 0;
			const available = row.original.motornet?.availableUnits ?? 0;
			return (
				<Badge
					bg="light"
					text="dark"
					className="border"
					title={i18next.t('vehicle_association_units_tooltip', {
						quotable,
						available,
						defaultValue: '{{quotable}} unità quotabili su {{available}} disponibili',
					})}
				>
					{quotable} / {available}
				</Badge>
			);
		},
	},
	{
		width: 90,
		id: 'matrixAutoRenew',
		Header: i18next.t('vehicle_association_matrix_auto_renew', { defaultValue: 'Rinnovo auto' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => <MatrixAutoRenewSwitch row={row.original} />,
	},
	{
		width: 380,
		id: 'actions',
		Header: i18next.t('actions', { defaultValue: 'Azioni' }),
		disableSortBy: true,
		Cell: ({ row }: CellProps) => (
			<div className="d-flex gap-1">
				{CHIPS.map((chip) => {
					const state = chipState(row.original, chip);
					const open = expanded?.versionKey === row.original.versionKey && expanded.chip === chip;
					// versionKey contiene ':', non valido nel selettore #id del tooltip
					const tooltipId = `chip-unavailable-${row.index}-${chip}`;
					return (
						<Fragment key={chip}>
							<Button
								id={state === 'unavailable' ? tooltipId : undefined}
								size="sm"
								variant={open ? 'dark' : CHIP_VARIANT[state]}
								className="rounded-pill text-uppercase text-nowrap flex-shrink-0 fw-semibold px-2 py-0"
								style={{ fontSize: 11, ...(state === 'manual' && !open ? { borderStyle: 'dashed' } : {}) }}
								title={state === 'unavailable' ? undefined : chipTitle(row.original, chip, state)}
								aria-pressed={open}
								onClick={(event) => {
									event.stopPropagation();
									onToggle(row.original.versionKey, chip);
								}}
							>
								{CHIP_LABELS[chip]} {CHIP_ICON[state]}
							</Button>
							{state === 'unavailable' && (
								<ToolTip
									anchorId={tooltipId}
									content={unavailableTitle(row.original, chip)}
									place="left"
									className="wide"
								/>
							)}
						</Fragment>
					);
				})}
			</div>
		),
	},
];
